<template>
  <NumeraiModal
    ref="modal"
    :dialog="dialog"
    :button="button"
    :title="viewOnly ? null : 'Validation Diagnostics'"
    :width="1100"
    :color="
      viewOnly ? 'var(--paper-background-color)' : 'var(--background-color)'
    "
    @close="close"
  >
    <div class="color-tertiary mb-3">
      This is a tool to compute and visualize the validation performance of your
      model.
      {{
        isAppClassic()
          ? 'Upload your model to automatically generate diagnostics or' +
            ' manually upload your validation predictions below.'
          : 'Upload your validation predictions below to generate diagnostics.'
      }}
      Predictions uploaded to Diagnostics will not affect your live submissions.
    </div>
    <div v-if="selectedModel !== null" class="">
      <div v-if="loading" class="full-width full-height space_around">
        <numerai-loader />
      </div>
      <div v-else class="d-flex full-width full-height">
        <div v-if="!viewOnly" class="side-bar mr-3">
          <v-select
            :items="modelSelectItems"
            :value="modelName"
            label="Select a model"
            class="flex-grow-0"
            @change="onModelChange"
          />
          <div class="d-flex justify-space-between my-2">
            <div v-if="!noUploads">
              <div
                class="text-link select_all color-tertiary"
                @click="clearAll"
              >
                {{ selectionToggleText }}
              </div>
            </div>
            <div v-if="diagnosticsSelected">
              <div
                v-if="!deleting"
                class="text-link select_all color-error"
                @click="deleteDiagnostics()"
              >
                Delete Selected
              </div>
              <v-progress-circular
                v-else
                indeterminate
                color="red"
                :size="20"
              ></v-progress-circular>
            </div>
          </div>
          <div v-if="fetching" class="loader">
            <NumeraiLoader />
          </div>
          <div v-else class="uploads_list_container">
            <div
              v-for="(upload, index) in uploads"
              :key="upload.id"
              class="upload_selector pa-2"
              :class="{ selected: index === uploadSelectedIndex }"
              @click="setUploadSelectedIndex(index)"
            >
              <div class="text_weight_500 mb-1">
                <input
                  :id="upload.id"
                  v-model="selectedDiagnostics"
                  type="checkbox"
                  :value="upload.id"
                  class="d-inline upload_checkbox"
                />
                <span class="filename">
                  {{ formatFilename(upload.filename) }}
                </span>
              </div>
              <div class="color-tertiary fileinfo">ID: {{ upload.id }}</div>
              <div class="color-tertiary fileinfo">
                {{ fromNow(upload.insertedAt) }}
              </div>
            </div>
          </div>
          <div class="mt-auto">
            <NumeraiUploadFile
              :auth-endpoint="
                isAppSignals()
                  ? $client.getDiagnosticsUploadAuthSignals
                  : $client.getDiagnosticsUploadAuth
              "
              :create-endpoint="
                isAppSignals()
                  ? $client.createDiagnosticsSignals
                  : $client.createDiagnostics
              "
              :model-id="selectedModel.id"
              success-text="Upload accepted"
              :on-success="uploadCompleted"
              :on-failure="fetchDiagnostics"
              :is-diagnostics="true"
            >
              <button
                class="numerai-button button-attention mt-1 pa-2 sidebar_button"
              >
                Upload Predictions
              </button>
            </NumeraiUploadFile>
            <DownloadData
              v-if="isAppClassic()"
              filename="v5.0/validation_example_preds.csv"
            >
              <button class="numerai-button mt-1 pa-2 sidebar_button">
                <i class="v-icon material-icons button-icon"> download </i
                >Example Predictions
              </button>
            </DownloadData>
            <DownloadData
              v-else
              filename="signals/v1.0/validation_example_preds.csv"
            >
              <button class="numerai-button mt-1 pa-2 sidebar_button">
                <i class="v-icon material-icons button-icon"> download </i>
                Download Example
              </button>
            </DownloadData>
          </div>
        </div>
        <div v-if="fetching" class="loader">
          <NumeraiLoader />
        </div>
        <div v-else class="d-flex flex-column flex-grow-1">
          <div
            v-if="!showDone"
            class="message-container"
            :class="{ 'numerai-paper': !viewOnly }"
          >
            <div v-if="noUploads" class="text-center color-tertiary message">
              <h2 class="pt-12">How to generate Diagnostics</h2>
              <div v-if="isAppClassic()">
                <div>
                  Upload your model to automatically generate diagnostics
                </div>
                <div class="pb-12">
                  or manually upload validation predictions on the left.
                </div>
              </div>
              <div v-else class="pb-12">
                Upload your validation predictions on the left to generate
                Diagnostics.
              </div>
            </div>
            <div v-if="diagnosticsRunning" class="text-center message">
              <numerai-loader />
              This upload is <strong>processing</strong>. Metrics should be
              ready in a minute.
            </div>
            <div v-else-if="showInvalid" class="text-center message">
              Your upload seems to be invalid:
              <p>{{ selectedUpload.message }}</p>
            </div>
            <div v-else-if="showFailed" class="text-center message">
              Something went wrong with your upload:
              <p>{{ selectedUpload.message }}</p>
              If you make a bug report, include this ID with it:
              <p>{{ selectedUpload.id }}</p>
            </div>
          </div>
          <div v-else-if="showDone">
            <div class="pa-4 mb-1" :class="{ 'numerai-paper': !viewOnly }">
              <div
                v-if="tb200 || selectedUpload.tb200Diagnostics"
                class="d-flex align-center justify-space-between mb-2"
              >
                <img class="navlogo" :src="logoPath" />
                <div class="model_name">{{ modelName }}</div>
              </div>
              <div>
                <DiagnosticsModalTable
                  v-if="isAppSignals()"
                  :uploads="uploads"
                  :tb200="tb200"
                  :upload-selected-index="uploadSelectedIndex"
                />
                <div v-else class="flexrow full-width justify-space-around">
                  <div
                    v-if="
                      selectedUpload.validationCorrSharpe ||
                        selectedUpload.validationCorrelation ||
                        selectedUpload.validationFeatureNeutralMean ||
                        selectedUpload.validationBmcMean
                    "
                    class="metrics"
                  >
                    <span class="metrics-title text_weight_600"
                      >Performance&nbsp;
                      <v-icon class="metrics-title-icon" small
                        >mdi-trending-up</v-icon
                      >
                    </span>

                    <SubmissionMetric
                      class="metric"
                      label="Sharpe Ratio"
                      :tooltip-text="validationSharpeTip"
                    >
                      <span>
                        {{ formatScore(selectedUpload.validationCorrSharpe) }}
                      </span>
                    </SubmissionMetric>

                    <SubmissionMetric
                      class="metric"
                      label="CORR20v2"
                      :tooltip-text="validationCorrelationTip"
                    >
                      <span>
                        {{ formatScore(selectedUpload.validationCorrMean) }}
                      </span>
                    </SubmissionMetric>

                    <!-- eslint-disable max-len -->
                    <SubmissionMetric
                      v-if="isAppClassic()"
                      class="metric"
                      label="FNCv3"
                      :tooltip-text="featureNeutralCorrelationV3Tip"
                    >
                      {{
                        formatScore(
                          selectedUpload.validationFeatureNeutralCorrV3Mean,
                        )
                      }}
                    </SubmissionMetric>

                    <SubmissionMetric
                      v-if="isAppClassic()"
                      class="metric"
                      label="tBMC"
                      :tooltip-text="bmcMeanTip"
                    >
                      {{ formatScore(selectedUpload.validationBmcMean) }}
                    </SubmissionMetric>
                  </div>

                  <div
                    v-if="
                      selectedUpload.validationCorrStd ||
                        selectedUpload.validationMaxFeatureExposure ||
                        selectedUpload.validationMaxDrawdown
                    "
                    class="metrics ml-6"
                  >
                    <span class="metrics-title text_weight_600">
                      Risk&nbsp;
                      <v-icon class="metrics-title-icon" small
                        >mdi-trending-down</v-icon
                      >
                    </span>

                    <SubmissionMetric
                      class="metric"
                      label="Std. Dev."
                      :tooltip-text="validationStdTip"
                    >
                      {{ formatScore(selectedUpload.validationCorrStd) }}
                    </SubmissionMetric>

                    <SubmissionMetric
                      v-if="isAppClassic()"
                      class="metric"
                      label="Feat. Exposure"
                      :tooltip-text="maxFeatureExposureTip"
                    >
                      {{ formatScore(selectedUpload.validationFeatureCorrMax) }}
                    </SubmissionMetric>

                    <SubmissionMetric
                      class="metric"
                      label="Max Drawdown"
                      :tooltip-text="maxDrawdownTip"
                    >
                      {{ formatScore(selectedUpload.validationMaxDrawdown) }}
                    </SubmissionMetric>
                  </div>

                  <div class="metrics ml-6">
                    <span class="metrics-title text_weight_600">
                      Other&nbsp;
                      <i
                        class="v-icon metrics-title-icon material-icons"
                        style="font-size: 18px"
                      >
                        bubble_chart
                      </i>
                    </span>

                    <SubmissionMetric
                      class="metric"
                      label="Autocorr"
                      :tooltip-text="autocorrTip"
                    >
                      {{ formatScore(selectedUpload.validationAutocorr) }}
                    </SubmissionMetric>

                    <SubmissionMetric
                      class="metric"
                      label="Ex. Preds Corr"
                      :tooltip-text="corrExampleTip"
                    >
                      {{ formatScore(selectedUpload.examplePredsCorrMean) }}
                    </SubmissionMetric>
                  </div>
                </div>
              </div>
            </div>
            <NumeraiChart
              v-if="selectedUpload && selectedUpload.status == 'done'"
              :header="(tb200 ? 'TB200 ' : '') + 'Performance'"
              :enable-cumsum="true"
              :enable-selector="true"
              :chart-options="{
                chartData,
                chartLabels,
              }"
            >
              <template #menu-items>
                <v-list-item>
                  <v-list-item-action>
                    <v-switch v-model="tb200" color="green" dense />
                  </v-list-item-action>
                  <v-list-item-title>TB200</v-list-item-title>
                </v-list-item>
              </template>
            </NumeraiChart>
          </div>
        </div>
      </div>
    </div>
  </NumeraiModal>
</template>

<script>
import { mapGetters } from 'vuex'
import NumeraiUploadFile from '@/components/shared/NumeraiUploadFile'
import NumeraiLoader from '@/components/shared/NumeraiLoader'
import SubmissionMetric from '@/components/shared/SubmissionMetric'
import {
  formatScore,
  formatPercent,
  isAppClassic,
  isAppSignals,
} from '@/lib/utils'
import NumeraiModal from '@/components/shared/NumeraiModal'
import NumeraiChart from '@/components/charts/NumeraiChart'
import moment from 'moment'
import DownloadData from '@/components/shared/DownloadData'
import DiagnosticsModalTable from '@/components/shared/DiagnosticsModalTable'

export default {
  components: {
    NumeraiChart,
    NumeraiUploadFile,
    NumeraiLoader,
    SubmissionMetric,
    NumeraiModal,
    DownloadData,
    DiagnosticsModalTable,
  },
  props: {
    text: { type: String, default: '' },
    diagnosticsId: { type: String, default: null },
    model: { type: Object, default: () => null },
    models: { type: Array, default: null },
    dialog: { type: Boolean, default: false },
    button: { type: Boolean, default: true },
    onClose: { type: Function, default: null },
    onDiagnosticGenerated: { type: Function, default: null },
  },
  data() {
    return {
      selectedModel: null,
      defaultModel: null,
      tb200: false,
      uploads: null,
      uploadSelectedIndex: null,
      fetching: true,
      deleting: false,
      firstLoad: true,
      enableTB200Tip:
        'Show performance of your top and bottom 200 predictions by round',
      disableTB200Tip: 'Show performance of your full predictions',
      maxFeatureExposureTip:
        'The maximum correlation your predictions have ' +
        'with any single feature. ',
      validationSharpeTip:
        'The mean of your per-era correlation divided by ' +
        'the standard deviation of your per-era correlation. ',
      validationCorrelationTip: 'The mean of your per-era correlations.',
      corrExampleTip:
        'The correlation between your model ' + 'and the example predictions.',
      featureNeutralCorrelationV3Tip:
        'The mean of your per-era correlations after your predictions ' +
        'have been neutralized to the 420 features in the medium subset ' +
        'of the V3 dataset',
      validationStdTip:
        'The standard deviation of per-era correlations between your ' +
        'predictions and the validation targets.',
      maxDrawdownTip:
        'The maximum difference from any local maxima to any subsequent' +
        ' minima for your cumulative sum of scores.',
      bmcMeanTip:
        'The mean of your per-era contribution to the' +
        ' v43_lgbm_ct_blend benchmark model.',
      autocorrTip: 'The autocorrelation of your per-era correlation.',
      selectedDiagnostics: [],
    }
  },
  computed: {
    ...mapGetters(['isDarkMode']),
    viewOnly() {
      return this.diagnosticsId
    },
    diagnosticsRunning() {
      return (
        this.selectedUpload &&
        (this.selectedUpload.status == 'queued' ||
          this.selectedUpload.status == 'valid')
      )
    },
    showInvalid() {
      return this.selectedUpload && this.selectedUpload.status == 'invalid'
    },
    showFailed() {
      return this.selectedUpload && this.selectedUpload.status == 'failed'
    },
    showDone() {
      return this.selectedUpload && this.selectedUpload.status == 'done'
    },
    modelSelectItems() {
      if (!this.models) {
        return []
      }
      return this.models.map(model => model.name.toUpperCase())
    },
    modelName() {
      return this.selectedModel && this.selectedModel.name.toUpperCase()
    },
    selectionToggleText() {
      if (this.selectedDiagnostics.length === 0) {
        return 'Select All'
      } else return 'Clear Selected'
    },
    logoPath() {
      if (this.isDarkMode) {
        if (isAppSignals()) {
          return require('@/assets/signals-logo-white.png')
        } else {
          return require('@/assets/Numerai-Logo-Side-White.png')
        }
      } else {
        if (isAppSignals()) {
          return require('@/assets/signals-logo-black.png')
        } else {
          return require('@/assets/Numerai-Logo-Side-Black.png')
        }
      }
    },
    loading() {
      // Only put the whole modal in loading mode on the first load
      return this.fetching && this.firstLoad
    },
    noUploads() {
      return !this.loading && (!this.uploads || this.uploads.length < 1)
    },
    selectedUpload() {
      if (!this.uploads) return null
      if (this.tb200) {
        if (this.uploads[this.uploadSelectedIndex]) {
          return this.uploads[this.uploadSelectedIndex].tb200Diagnostics
        } else {
          return null
        }
      }
      return this.uploads[this.uploadSelectedIndex]
    },
    chartData() {
      let obj = {}
      if (isAppClassic()) {
        obj = {
          CORR20v2: [[]],
          FNCv3: [[]],
          BMC: [[]],
        }
      } else if (isAppSignals()) {
        obj = {
          FNCv4: [[]],
          RIC: [[]],
          CORRv4: [[]],
          ICv2: [[]],
        }
      }
      return this.selectedUpload.perEraDiagnostics.reduce((acc, p) => {
        if (isAppClassic()) {
          acc.CORR20v2[0].push({
            x: p.era,
            y: p.validationCorr,
          })
          acc.FNCv3[0].push({
            x: p.era,
            y: p.validationFeatureNeutralCorrV3,
          })
          acc.BMC[0].push({
            x: p.era,
            y: p.validationBmc,
          })
        } else if (isAppSignals()) {
          acc.FNCv4[0].push({
            x: p.era,
            y: p.validationFncV4,
          })
          acc.RIC[0].push({
            x: p.era,
            y: p.validationRic,
          })
          acc.CORRv4[0].push({
            x: p.era,
            y: p.validationCorrV4,
          })
          acc.ICv2[0].push({
            x: p.era,
            y: p.validationIcV2,
          })
        }
        return acc
      }, obj)
    },
    chartLabels() {
      return this.selectedUpload.perEraDiagnostics.map(p => {
        return p.era
      })
    },
    diagnosticsSelected() {
      return this.selectedDiagnostics.length > 0
    },
  },
  watch: {
    dialog: function(newVal) {
      if (newVal) {
        this.reset()
        this.defaultModel = this.model
        this.selectedModel = this.model
      }
    },
    model: function(newModel) {
      if (!this.dialog) {
        return
      }
      this.reset()
      this.defaultModel = newModel
      this.selectedModel = newModel
      this.fetchDiagnostics()
    },
  },
  created() {
    this.defaultModel = this.model
    this.selectedModel = this.model
    this.fetchDiagnostics()
  },
  methods: {
    formatScore,
    formatPercent,
    isAppClassic,
    isAppSignals,
    reset() {
      this.selectedModel = null
      this.tb200 = false
      this.uploads = null
      this.uploadSelectedIndex = null
      this.fetching = false
      this.deleting = false
      this.firstLoad = true
      this.selectedDiagnostics = []
    },
    close() {
      this.reset()
      this.onClose && this.onClose()
    },
    onModelChange(modelName) {
      this.selectedModel = this.models.filter(
        model => model.name.toUpperCase() === modelName,
      )[0]
      this.fetchDiagnostics()
    },
    clearAll() {
      if (this.selectedDiagnostics.length === 0) {
        for (const upload of this.uploads) {
          this.selectedDiagnostics.push(upload.id)
        }
      } else this.selectedDiagnostics = []
    },
    toggleTb200() {
      this.tb200 = !this.tb200
    },
    async fetchDiagnostics() {
      if (!this.selectedModel) {
        return
      }
      this.fetching = true
      try {
        let res = await this.$client.getDiagnostics(
          this.diagnosticsId,
          this.selectedModel.id,
        )
        this.uploads = res.filter(upload => upload.top_bottom == null)
        if (this.uploads && this.uploads.length > 0) {
          this.uploadSelectedIndex = 0
        }
        if (this.diagnosticsId) {
          const index = this.uploads.findIndex(
            upload => upload.id === this.diagnosticsId,
          )
          if (index === -1) {
            this.uploadSelectedIndex = 0
            throw new Error('diagnosticsId for model does not exist')
          } else {
            this.uploadSelectedIndex = index
          }
        }
      } catch (err) {
        this.errorMessage = err
      } finally {
        this.fetching = false
        if (this.firstLoad) {
          this.firstLoad = false
        }
      }
    },
    uploadCompleted() {
      this.fetchDiagnostics()

      this.onDiagnosticGenerated && this.onDiagnosticGenerated()

      // fetch diagnostics in a minute
      // so queued things can get refreshed
      // TODO: Could add id param on fetchDiagnostics to only get the one right
      // diagnostic

      // Fetch diagnostics a few times over the next four minutes
      setTimeout(() => {
        this.fetchDiagnostics()
      }, 60000)
      setTimeout(() => {
        this.fetchDiagnostics()
      }, 90000)
      setTimeout(() => {
        this.fetchDiagnostics()
      }, 240000)
    },
    setUploadSelectedIndex(_index) {
      this.uploadSelectedIndex = _index
    },
    // Remove the whacky characters appended to upload filenames
    formatFilename(_filename) {
      const typeIndex = _filename.lastIndexOf('.')
      const type = _filename.slice(typeIndex)
      const name = _filename.slice(0, typeIndex - 13)
      return name + type + ''
    },
    fromNow(_date) {
      return moment(_date).fromNow()
    },
    async deleteDiagnostics() {
      this.deleting = true
      try {
        let tb200DiagnosticsIds = this.uploads
          .filter(
            upload =>
              this.selectedDiagnostics.includes(upload.id) &&
              !!upload.tb200Diagnostics,
          )
          .map(upload => upload.tb200Diagnostics.id)
        let idsToDelete = this.selectedDiagnostics.concat(tb200DiagnosticsIds)
        await this.$client.deleteDiagnostics(idsToDelete)
      } catch (err) {
        console.error(err)
      } finally {
        // filter out the deleted diagnostics
        await this.fetchDiagnostics()
        this.selectedDiagnostics = []
        this.deleting = false
      }
    },
  },
}
</script>
<style scoped>
.side-bar {
  max-width: 285px;
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

.performance-chart {
  display: flex;
  flex-direction: column;
  align-self: stretch;
}

::v-deep .v-input__slot {
  margin: 0;
}
::v-deep .v-text-field__details {
  display: none;
}

.message-container {
  height: 100%;
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}

.message {
  margin: auto 0;
}

.loader {
  margin: 50px auto;
  text-align: center;
}
.spinner {
  width: 40px;
  height: 40px;
  margin: 0 auto;
  background-color: var(--color-primary);
  border-radius: 100%;
  -webkit-animation: scaleout 1s infinite ease-in-out;
  animation: scaleout 1s infinite ease-in-out;
}

.text {
  color: var(--color-tertiary);
  margin-top: 0.4em;
}

.upload_selector {
  text-align: left;
  cursor: pointer;
  color: var(--color-tertiary);
  border-bottom: 1px solid var(--color-very-subtle);
}

.upload_selector:hover {
  color: var(--color-secondary);
  background-color: var(--paper-background-color);
}

.upload_selector.selected {
  background-color: var(--paper-background-color);
  color: var(--color-primary);
}

.filename {
  overflow-wrap: break-word;
  line-height: 1.2;
  vertical-align: text-bottom;
}
.fileinfo {
  font-size: 0.5rem;
}

.uploads_list_container {
  overflow-y: auto;
  max-height: 400px;
}

.metrics-title-icon {
  margin-right: 8px;
  margin-bottom: 4px;
  color: var(--color-secondary);
}
.metrics-title {
  display: inline-flex;
  color: var(--color-secondary);
  font-weight: 500;
  font-size: 1.2em;
}
.metric {
  margin-bottom: 6px;
  min-width: 150px;
  font-size: 13px;
  border-bottom: 1px dotted var(--color-tertiary);
}
.metrics {
  display: flex;
  flex-direction: column;
  flex-grow: 1;
}
.metrics:last-child .metric:last-child {
  margin-bottom: 0px;
}

.navlogo {
  height: 30px;
}

.divider {
  border-top: 1px solid var(--paper-border-color);
  height: 18px;
}

.model_name {
  font-family: var(--brand-font);
  text-transform: uppercase;
}

.sidebar_button {
  width: 100%;
  font-size: 0.9em;
}

.modal_checkbox {
  font-family: var(--ui-font);
  font-size: 0.8em;
  font-weight: 500;
  padding: 2px 4px 2px 2px;
  margin: 2px;
}

.select_all {
  font-size: 0.8em;
}

.selection-buttons {
  min-height: unset;
  min-width: 64px;
  height: 34px;
  font-family: var(--ui-font);
  text-transform: uppercase;
  font-weight: 600;
  font-size: 12px;
  border-radius: 3px;
}
.selection-buttons-group-container {
  display: inline-flex;
}
.selection-buttons-group {
  border-radius: unset;
}
.selection-buttons-group:first-of-type {
  border-radius: 3px 0 0 3px !important;
  border-right: 0;
}
.selection-buttons-group:last-of-type {
  border-radius: 0 3px 3px 0 !important;
  border-left: 0;
}
.button-selected {
  background-color: var(--color-button-background);
  color: var(--color-button-text);
}
.button-selected:hover {
  background-color: var(--color-button-background);
  color: var(--color-button-text);
}
</style>
